import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../material/material.module';
import { PipesModule } from '../../pipes/pipes.module';
import { AuthService } from '../../services/auth/auth.service';
import { User } from '../../models/user';
import { UserService } from '../../services/user/user.service';
import { MatChipsComponent } from '../mat-chips/mat-chips.component';
import { MatChipsModule } from '@angular/material/chips';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldComponent } from '../mat-form-field/mat-form-field.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { TableComponent } from '../table/table.component';
import { UploadImageComponent } from '../upload-image/upload-image.component';
import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import { PagingComponent } from '../paging/paging.component';
import { CategoryService } from '../../services/category/category.service';
import { ClientsComponent } from '../../project/clients/clients.component';
import { DocumentReference } from '@angular/fire/compat/firestore';
import { AssignedCategoryService } from '../../services/assigned-category/assigned-category.service';
import { AssignedCategory } from '../../models/assignedCategory';

@Component({
	selector: 'app-manage-clients-modal',
	standalone: true,
	imports: [
		MatFormFieldComponent,
		CommonModule,
		MatFormFieldModule,
		MatInputModule,
		MatIconModule,
		FormsModule,
		MatButtonModule,
		TableComponent,
		UploadImageComponent,
		MatDialogModule,
		MatChipsComponent,
		MatChipsModule,
		LanguageSelectorComponent,
		PagingComponent,
		MatChipsComponent,
		MatChipsModule,
		MatChipsComponent,
		MaterialModule,
		PipesModule,
		MatSelectModule,
		ClientsComponent
	],
	templateUrl: './manage-clients-modal.component.html',
	styleUrl: './manage-clients-modal.component.scss',
	schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ManageClientsModalComponent implements OnInit {

	user = this.authService.auth.user;
	selectedContentCreators: User[] = [];
	searchTerm: string = '';
	contentCreatorsActive: (User & { __alreadyAssignedTo?: boolean })[] = [];
	filteredContentCreators: User[] = [];

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: any,
		private dialogRef: MatDialogRef<ManageClientsModalComponent>,
		private authService: AuthService,
		private userService: UserService,
		private categoryService: CategoryService,
		private assignedCategoryService: AssignedCategoryService
	) { }

	async ngOnInit(): Promise<void> {
		await this.getClients();
		await this.getSelectedContentCreators();
		this.updateFilteredContentCreators();
	}

	async getClients() {
		this.contentCreatorsActive = await this.userService.getActiveContentCreators(this.authService.projectId);
		this.updateFilteredContentCreators();
	}

	async getSelectedContentCreators() {
		this.selectedContentCreators = await this.assignedCategoryService.getAssignedClientsOnCategory(this.data?.category.id, this.authService.projectId);
		const categoryId = this.data?.category.id;
		const projectId = this.authService.projectId;

		const promises = this.selectedContentCreators.map(async creator => {
			const assigned = await this.assignedCategoryService.getCurrentAssignedCategory(creator.id, categoryId, projectId);
			creator.__assignedCategories = [assigned];
			creator.__previouslyAssigned = true;
		});
		await Promise.all(promises);
	}

	displayContentCreator(creator: any): string {
		return creator?.username;
	}

	removeCreator(creator: User): void {
		this.selectedContentCreators = this.selectedContentCreators.filter(c => c !== creator);
		this.updateFilteredContentCreators();
	}

	updateFilteredContentCreators(searchTerms: string = ''): void {
		const searchTerm = searchTerms?.toLowerCase();
		const filtered = this.contentCreatorsActive?.filter(creator =>
			creator?.username?.toLowerCase().includes(searchTerm)
		);

		const selectedIds = new Set(this.selectedContentCreators.map(c => c.id));
		this.filteredContentCreators = this.selectedContentCreators.concat(
			filtered?.filter(creator => !selectedIds.has(creator.id)) || []
		);
	}

	onSearchTermChange(term: string): void {
		this.searchTerm = term;
		this.updateFilteredContentCreators(term);
	}

	dismiss() {
		this.dialogRef.close(null);
	}

	decline() {
		this.dialogRef.close(false);
	}

	async accept() {
		const creatorsToAssign = this.selectedContentCreators.filter(creator => !(creator?.__previouslyAssigned && !creator.__assignedCategories?.[0]?.__modified));
		await Promise.all(creatorsToAssign.map(async creator => {
			await this.assignCategoryToContentCreator(creator);
			await this.linkCategoryWithCreator(creator);
		}));

		await this.categoryService.updateAssignedCategories(this.data?.category, this.authService.projectId);
		this.dialogRef.close(true);
	}

	async linkCategoryWithCreator(creator: User) {
		await this.categoryService.linkCategoryWithUser(this.data.category.id, creator.username, this.authService.projectId, creator.id);
	}

	async assignCategoryToContentCreator(creator: User) {
		const ref: DocumentReference = this.categoryService.getDocumentReferenceById(this.data.category.id, this.authService.projectId);
		let categoryToAssign = creator.__assignedCategories?.[0] ?? new AssignedCategory();
		categoryToAssign.id = this.data.category.id;
		categoryToAssign.category = ref;
		categoryToAssign.name = this.data.category.name;
		categoryToAssign.nameLowercase = this.data.category.name.toLocaleLowerCase();
		categoryToAssign.image = this.data.category.image;
		categoryToAssign.status = this.data.category.status;
		await this.assignedCategoryService.addAssignedCategory(categoryToAssign, this.authService.projectId, creator.id);
	}
}
